import { Fragment, useState, useEffect } from 'react'
import { getClient } from '@lib/sanity'
import { loadTranslation } from '@lib/i18n'
import { groq } from 'next-sanity'
import { Trans, t } from '@lingui/macro'
import H1 from 'components/ui/H1'
import Header from 'components/ui/Header'
import MemberCard from 'components/MemberCard'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { CheckIcon, SelectorIcon } from '@heroicons/react/solid'
import { Listbox, Transition } from '@headlessui/react'
import { useTranslations } from 'providers/TranslationsProvider'
import MemberHighlight from 'components/MemberHighlight'
import Hr from 'components/ui/Hr'
import LeadingParagraph from 'components/ui/LeadingParagraph'

const MemberMap = dynamic(() => import('components/MemberMap'), { ssr: false })

const Members = ({ items, regions, totalMembers }) => {
  const { locale } = useRouter()
  const { init, headLinks } = useTranslations()
  const [region, setRegion] = useState(null)
  const [country, setCountry] = useState(null)
  const [selectedRegionLabel, setSelectedRegionLabel] = useState(
    t`Filter by region`
  )

  useEffect(() => {
    init()
  }, [])

  useEffect(() => {
    const r = regions.filter((reg) => reg._id === region)
    if (r.length) {
      setSelectedRegionLabel(r[0].title[locale])
    } else {
      setSelectedRegionLabel(t`Filter by region`)
    }
  }, [region, locale, regions])

  const selectCountry = (countryCode) => {
    setRegion(null)
    setCountry(countryCode)
  }

  const selectRegion = (regionId) => {
    setRegion(regionId)
    setCountry(null)
  }

  const Items = () => {
    let filteredItems = [...items]
    if (region) {
      filteredItems = items.filter((item) => {
        let regs = []
        if (item.region) {
          regs = item.region.filter((reg) => {
            return reg._id === region
          })
        }
        return !!regs.length
      })
    } else if (country) {
      filteredItems = items.filter((item) => item.country === country)
    }

    return filteredItems.map((member) => (
      <MemberCard key={member._id} member={member} />
    ))
  }

  return (
    <div className="bg-accent-200">
      <Head>
        <title key="title">
          {`${t`IDPC Members`} - ${process.env.NEXT_PUBLIC_SITE_TITLE}`}
        </title>
        <meta property="og:title" content={t`IDPC Members`} key="og:title" />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_DOMAIN}${
            locale == 'en' ? `` : `/${locale}`
          }/members`}
          key="canonical"
        />
        <meta
          property="og:url"
          content={`${process.env.NEXT_PUBLIC_DOMAIN}${
            locale == 'en' ? `` : `/${locale}`
          }/members`}
          key="og:url"
        />
        {headLinks()}
      </Head>
      <div className="container">
        <Header>
          <H1>
            <Trans>Members</Trans>
          </H1>
        </Header>
        <LeadingParagraph>
          <Trans>
            IDPC&apos;s global, diverse and ever-growing membership is the
            network&apos;s biggest strength. Spanning a wide range of areas of
            expertise, our {totalMembers} members orientate our work and
            strategic direction.
          </Trans>
        </LeadingParagraph>
        <MemberHighlight />
        <Hr />
        <MemberMap items={items} country={country} setCountry={selectCountry} />
        <div className="pb-10">
          <div className="mb-4 w-full md:w-72">
            <Listbox value={region} onChange={selectRegion}>
              <div className="relative mt-1">
                <Listbox.Button className="relative w-full cursor-default rounded-lg bg-white py-3 pl-3 pr-10 text-left shadow-md focus:outline-none focus-visible:border-primary-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-accent-500 sm:text-sm">
                  <span className="block truncate">{selectedRegionLabel}</span>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    <SelectorIcon
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </span>
                </Listbox.Button>
                <Transition
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                    <Listbox.Option
                      key={`none`}
                      value={null}
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 pl-10 pr-4 ${
                          active
                            ? 'bg-accent-200 text-primary-500'
                            : 'text-primary-500'
                        }`
                      }
                    >
                      ---
                    </Listbox.Option>
                    {regions.map((reg) => (
                      <Listbox.Option
                        key={reg._id}
                        value={reg._id}
                        className={({ active }) =>
                          `relative cursor-default select-none py-2 pl-10 pr-4 ${
                            active
                              ? 'bg-accent-200 text-primary-500'
                              : 'text-primary-500'
                          }`
                        }
                      >
                        {({ selected }) => (
                          <>
                            <span
                              className={`block truncate ${
                                selected ? 'font-medium' : 'font-normal'
                              }`}
                            >
                              {reg.title[locale]}
                            </span>
                            {selected ? (
                              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-accent-600">
                                <CheckIcon
                                  className="h-5 w-5"
                                  aria-hidden="true"
                                />
                              </span>
                            ) : null}
                          </>
                        )}
                      </Listbox.Option>
                    ))}
                  </Listbox.Options>
                </Transition>
              </div>
            </Listbox>
          </div>
          {items?.length && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {Items()}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export const getStaticProps = async ({ locale, params, preview = false }) => {
  const translation = await loadTranslation(
    locale,
    process.env.NODE_ENV === 'production'
  )
  const queries = [
    `"members": *[_type == "member"] | order(lower(name) asc){_id, name, slug, mainImage, country, region[]->{_id, title, slug}}`,
    `"regions": *[_type == "region"] | order(lower(slug) asc){_id, title, slug}`,
    `"totalMembers": count(*[_type == "member"])`,
  ]
  const data = await getClient(preview).fetch(
    groq`{
      ${queries.join(',\n')}
    }`,
    {
      locale,
    }
  )

  return {
    props: {
      translation,
      items: data.members,
      regions: data.regions,
      totalMembers: data.totalMembers,
      preview,
    },
    revalidate: 60 * 5, // Every five mins
  }
}

export default Members
